import * as React from 'react';
import Table from 'react-bootstrap/Table';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Pagination from '@mui/material/Pagination';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import { postRequest } from '../../lib/api';
import { AuthContext } from '../../authContext/context';
import { useContext, useEffect, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import CloseIcon from '@mui/icons-material/Close';
import Modal from 'react-bootstrap/Modal';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import { useParams , useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import Alert from '@mui/material/Alert'
import BreadcrumbsWrapper from '../../components/breadcrumbs'

// =========================== MODAL ===========================
function MyVerticallyCenteredModal({ show, onHide, authToken, onCustomerAdd }) {
  const [searchFields, setSearchFields] = useState({
    title: '',
    forename: '',
    surname: '',
    email: '',
    phone_number: ''
  });
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef();

  const fetchCustomerSearch = async () => {
    const isEmpty = Object.values(searchFields).every(v => !v);
    if (isEmpty) {
      setSearchResults([]);
      return;
    }



    try {
      setLoading(true);
      const res = await postRequest('private/leadsCustomerList', searchFields, authToken);
      setSearchResults(res?.data || []);
    } catch (err) {
      console.error('Error fetching search:', err);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchCustomerSearch, 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchFields]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSearchFields((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateCustomer = async () => {
    try {
      setLoading(true);
      const res = await postRequest('private/createCustomer', searchFields, authToken);
      if (res?.status === 200 || res?.status === 'success' || (res?.data && res.data._id)) {
        Swal.fire('Success', 'Customer created successfully!', 'success');
        const newCustomer = res.data;
        onCustomerAdd(newCustomer);
        onHide();
        setSearchFields({ title: '', forename: '', surname: '', email: '', phone_number: '' });
      }
    } catch (err) {
      Swal.fire('Error', err?.message || 'Something went wrong', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Customers</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className='m-0'>
          {['title', 'forename', 'surname', 'email', 'phone_number'].map((field, idx) => (
            <Col xs={field === 'email' || field === 'phone_number' ? 6 : 4} key={idx}>
              <Box sx={{ '& > :not(style)': { m: 1, width: '100%' } }}>
                <TextField
                  label={field.charAt(0).toUpperCase() + field.slice(1).replace('_', ' ')}
                  name={field}
                  value={searchFields[field]}
                  onChange={handleChange}
                  variant="outlined"
                  type={field === 'email' ? 'email' : field === 'phone_number' ? 'tel' : 'text'}
                />
              </Box>
            </Col>
          ))}
        </Row>

        <Row className='mt-4'>
          <Col>
            <Table responsive bordered>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Add</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="4" className='text-center'>Loading...</td></tr>
                ) : searchResults.length > 0 ? (
                  searchResults.map((customer) => (
                    <tr key={customer._id}>
                      <td>{customer.title} {customer.forename} {customer.surname}</td>
                      <td>{customer.email}</td>
                      <td>{customer.phone_number}</td>
                      <td>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            onCustomerAdd(customer);
                            onHide();
                          }}
                        >
                          Add
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan="4" className='text-center'>No customers found</td></tr>
                )}
              </tbody>
            </Table>
          </Col>
        </Row>
        {!loading && searchResults.length === 0 && Object.values(searchFields).some(v => v) && (
          <Row className='mt-3'>
            <Col className='text-center'>
              <Button
                variant="contained"
                startIcon={<PersonAddOutlinedIcon />}
                onClick={handleCreateCustomer}
                sx={{
                  backgroundColor: '#6f42c1',
                  '&:hover': {
                    backgroundColor: '#5a32a3'
                  }
                }}
              >
                Add Customer
              </Button>
            </Col>
          </Row>
        )}
      </Modal.Body>
    </Modal>
  );
}

// ======================= MAIN COMPONENT ========================
export const EstateAdminstration = () => {
  const [customerData, setCustomerData] = useState(null);
  const [groupCodes, setGroupCodes] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [modalShow, setModalShow] = useState(false);
  const [customerError, setCustomerError] = useState(false); // ✅ for error on empty customer selection
  const [estateCosts, setEstateCosts] = useState(null);
  const [latestBranchId, setLatestBranchId] = useState(null);
  const [lenderPanels, setLenderPanels] = useState([]);
  const [partners, setPartners] = useState([]);

  const { customerId, slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const authCtx = useContext(AuthContext);
   const productId = location.state?.productId;

  // Get customerId from URL query params
  const [searchParams] = useSearchParams();
  const customerIdFromUrl = searchParams.get('customerId');

     const slugToTitle = (slug) => {
  return slug
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

  const readableTitle = slugToTitle(slug || '');

  const {
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      group_code: '',
      acquisition: 'Inbound',
      net_value: '',
      lender_panel_id: '',
      partner_id: '',
    },
    mode: 'onTouched',
  });
  
  // Watch net_value for real-time cost calculation preview
  const watchedNetValue = watch('net_value');

  const handleCustomerAdd = (customer) => {
    if (!selectedCustomers.some(c => c._id === customer._id)) {
      setSelectedCustomers(prev => [...prev, customer]);
      setCustomerError(false); // ✅ reset error
    }
  };

  const fetchGroupCodes = async () => {
    try {
      const res = await postRequest('private/getGroupCodeList', {}, authCtx.token);
      if (res?.status === 200 && Array.isArray(res?.data)) {
        setGroupCodes(res.data);
      }
    } catch (err) {
      console.error("Failed to fetch group codes:", err);
    }
  };

  // Fetch active lender panels
  const fetchActiveLenderPanels = async () => {
    try {
      const response = await postRequest('private/getActiveLenderPanels', {}, authCtx.token);
      if (response?.status === 200 && response?.data) {
        setLenderPanels(response.data);
      }
    } catch (error) {
      console.error('Error fetching lender panels:', error);
    }
  };

  // Fetch partners
  const fetchPartners = async () => {
    try {
      console.log('📋 Fetching partners...');
      const response = await postRequest('private/partnerList', { page: 1, limit: 100 }, authCtx.token);
      console.log('📋 Partners Response:', response);
      if (response?.data?.docs) {
        console.log('✅ Partners fetched:', response.data.docs);
        setPartners(response.data.docs);
      } else {
        console.log('⚠️ No partners data in response');
      }
    } catch (error) {
      console.error('❌ Error fetching partners:', error);
    }
  };

  const fetchCustomerDetails = async (id) => {
    try {
      const response = await postRequest(`private/getCustomerIndividualData/${id}`, {}, authCtx.token);
      if (response?.status === 200 && response?.data) {
        setCustomerData(response.data);
      }
    } catch (err) {
      console.error('Error fetching customer details', err);
    }
  };

  // Fetch estate administration costs from the latest branch
  // const fetchEstateAdminCosts = async () => {
  //   if (!productId) return;
  //
  //   console.log('=== FETCHING ESTATE ADMIN COSTS FOR NEW LEAD ===');
  //   console.log('Product ID:', productId);
  //
  //   try {
  //     // First get all branches to find which one has Estate Administration configured
  //     const branchesResponse = await postRequest('private/getAllBranches', {}, authCtx.token);
  //
  //     if (branchesResponse.status === 200 && branchesResponse.data) {
  //       console.log('Total branches found:', branchesResponse.data.length);
  //
  //       // Check each branch for Estate Administration configuration
  //       for (const branch of branchesResponse.data) {
  //         const branchName = branch.name || branch.branch_name || branch.title || `Branch_${branch._id}`;
  //         console.log(`Checking branch: ${branchName}`);
  //
  //         try {
  //           const estateResponse = await postRequest('private/getEstateAdministrationCost', {
  //             product_id: productId,
  //             branch_id: branch._id
  //           }, authCtx.token);
  //
  //           if (estateResponse.status === 'success' && estateResponse.data && estateResponse.data.costBreakdowns) {
  //             // Found the branch with Estate Administration
  //             console.log('✅ FOUND Estate Administration in branch:', branchName);
  //             console.log('Estate data:', estateResponse.data);
  //
  //             setEstateCosts(estateResponse.data);
  //             setLatestBranchId(branch._id);
  //             return; // Found it, exit the loop
  //           } else {
  //             console.log('❌ No Estate Administration in branch:', branchName);
  //           }
  //         } catch (err) {
  //           console.log(`Error checking branch ${branchName}:`, err.response?.data?.message);
  //         }
  //       }
  //
  //       // If we reach here, no branch has Estate Administration configured
  //       console.log('❌ No branch found with Estate Administration configuration');
  //       setEstateCosts(null);
  //     }
  //
  //   } catch (err) {
  //     console.error('Error finding Estate Administration branch:', err);
  //     setEstateCosts(null);
  //   }
  // };

  // Function to calculate costs based on net value
  // const calculateCosts = (netValue) => {
  //   if (!netValue || !estateCosts || !estateCosts.costBreakdowns) {
  //     return null;
  //   }
  //
  //   // Determine complexity based on net estate value
  //   let complexity = 'Low';
  //   if (netValue >= 650000) {
  //     complexity = 'High';
  //   } else if (netValue >= 325000) {
  //     complexity = 'Medium';
  //   }
  //
  //   console.log('=== CALCULATING COSTS ===');
  //   console.log('Net Value:', netValue);
  //   console.log('Calculated Complexity:', complexity);
  //
  //   // Find the cost breakdown for this complexity
  //   const costBreakdown = estateCosts.costBreakdowns.find(
  //     breakdown => breakdown.complexity === complexity
  //   );
  //
  //   if (costBreakdown) {
  //     console.log('Found matching cost breakdown:', costBreakdown);
  //     return {
  //       complexity: complexity,
  //       mlmLegalCost: costBreakdown.mlmLegalCost || 0,
  //       vat: costBreakdown.vat || 0,
  //       totalCost: costBreakdown.totalCost || 0,
  //       rangeLabel: costBreakdown.rangeLabel || ''
  //     };
  //   }
  //
  //   return null;
  // };

  useEffect(() => {
    fetchGroupCodes();
    if (customerId) fetchCustomerDetails(customerId);
  }, [customerId]);

  // useEffect(() => {
  //   if (productId) {
  //     fetchEstateAdminCosts();
  //   }
  // }, [productId]);

  // Fetch active lender panels and partners on mount
  useEffect(() => {
    fetchActiveLenderPanels();
    fetchPartners();
  }, [authCtx.token]);

  // Fetch initial customer from URL and add to selectedCustomers
  useEffect(() => {
    const fetchInitialCustomer = async () => {
      if (customerIdFromUrl && authCtx.token && selectedCustomers.length === 0) {
        console.log('Fetching initial customer from URL:', customerIdFromUrl);
        try {
          const customerRes = await postRequest(`private/getCustomerIndividualData/${customerIdFromUrl}`, {}, authCtx.token);
          if (customerRes?.data) {
            console.log('Fetched initial customer:', customerRes.data);
            setSelectedCustomers([customerRes.data]);
            setCustomerError(false);
          }
        } catch (err) {
          console.error('Error fetching initial customer:', err);
        }
      }
    };

    fetchInitialCustomer();
  }, [customerIdFromUrl, authCtx.token]);

  const onSubmit = async (formData) => {
   
    if (selectedCustomers.length === 0) {
      setCustomerError(true);
      return;
    }

    const created_id = localStorage.getItem('id')

    // Calculate costs based on net value
    // const netValue = parseFloat(formData.net_value);
    // const costs = calculateCosts(netValue);
    //
    // console.log('=== CREATING LEAD WITH CALCULATED COSTS ===');
    // console.log('Net Value:', netValue);
    // console.log('Calculated Costs:', costs);

    const payload = {
      ...formData,
      product_id:productId,
      created_id:created_id,
      customer_ids: selectedCustomers.map(c => c._id),
      lender_panel_id: formData.lender_panel_id || null,
      partner_id: formData.partner_id || null,
    };

    console.log('🚀 PAYLOAD BEING SENT TO CREATE LEAD:');
    console.log('  - partner_id from formData:', formData.partner_id);
    console.log('  - partner_id in payload:', payload.partner_id);
    console.log('  - lender_panel_id from formData:', formData.lender_panel_id);
    console.log('  - Full payload:', payload);

    // Add calculated costs to payload if available
    // if (costs) {
    //   payload.complexity = costs.complexity;
    //   payload.mlmLegalCost = costs.mlmLegalCost;
    //   payload.vat = costs.vat;
    //   payload.totalCost = costs.totalCost;
    //   payload.rangeLabel = costs.rangeLabel;
    //   payload.branch_id = latestBranchId; // Include the branch ID that has the config
    //
    //   console.log('✅ Including calculated costs in payload:', {
    //     complexity: costs.complexity,
    //     mlmLegalCost: costs.mlmLegalCost,
    //     vat: costs.vat,
    //     totalCost: costs.totalCost,
    //     branch_id: latestBranchId
    //   });
    // } else {
    //   console.log('⚠️ No estate admin costs found - lead will be created without cost calculations');
    // }

    try {
      const res = await postRequest('private/createLeads', payload, authCtx.token);
      if (res?.status == 'success') {
        Swal.fire('Success', 'Leads created successfully!', 'success');
        reset();
        setSelectedCustomers([]);
        navigate('/leads');
      } else {
        Swal.fire('Error', res?.message || 'Failed to create leads', 'error');
      }
    } catch (err) {
      Swal.fire('Error', err?.message || 'Something went wrong', 'error');
    }
  };

  return (
    <>
      <BreadcrumbsWrapper
       items={[
       { label: 'Dashboard', path: '/dashboard' },
        { label: 'Customers', path: '/customers' },
        { label: 'Estate Adminstration', path: '' },
          ]}
            />
           <br></br>

      <div className='page-header pb-3'>
        <h1 className='mt-0 mb-0'>Create {readableTitle}
          <small className='color-grey'> {customerData ? `${customerData.forename || ''} ${customerData.surname || ''}` : ''}</small>
        </h1>
      </div>

      <div className='page-top-formview mb-3'>
        <Form onSubmit={handleSubmit(onSubmit)}>

          <Row className="mb-3">
            <Col xs={12} md={6}>
              <h5 className="mb-3" style={{ fontWeight: 600, color: '#2c3e50' }}>Group Code</h5>
              <Controller
                name="group_code"
                control={control}
                rules={{ required: 'Group Code is required' }}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.group_code}>
                    <InputLabel>Group Code</InputLabel>
                    <Select {...field} label="Group Code">
                      {groupCodes.map((item) => (
                        <MenuItem key={item.code} value={item._id}>
                          {item.name} ({item.code})
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.group_code && (
                      <p style={{ color: 'red', fontSize: '0.8rem', marginTop: '4px' }}>{errors.group_code.message}</p>
                    )}
                  </FormControl>
                )}
              />
            </Col>

            <Col xs={12} md={6}>
              <h5 className="mb-3" style={{ fontWeight: 600, color: '#2c3e50' }}>Acquisition</h5>
              <Controller
                name="acquisition"
                control={control}
                rules={{ required: 'Acquisition is required' }}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.acquisition}>
                    <InputLabel>Acquisition</InputLabel>
                    <Select {...field} label="Acquisition">
                      <MenuItem value='Inbound'>Inbound</MenuItem>
                      <MenuItem value='Cold-Acquistion'>Cold-Acquistion</MenuItem>
                    </Select>
                    {errors.acquisition && (
                      <p style={{ color: 'red', fontSize: '0.8rem', marginTop: '4px' }}>{errors.acquisition.message}</p>
                    )}
                  </FormControl>
                )}
              />
            </Col>
          </Row>

          <Row className="mb-3">
            <Col xs={12}>
              <h5 className="mb-3" style={{ fontWeight: 600, color: '#2c3e50' }}>Net Estate Value</h5>
              <Controller
                name="net_value"
                control={control}
                rules={{
                  required: 'Net Estate Value is required',
                  validate: val => val > 0 || 'Value must be greater than 0'
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Net Estate Value"
                    sx={{ width: '100%' }}
                    type="number"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">£</InputAdornment>,
                    }}
                    error={!!errors.net_value}
                    helperText={errors.net_value?.message}
                  />
                )}
              />
            </Col>
          </Row>

          {/* Lender Panel & Partner Section */}
          <Row className="mb-3">
            <Col xs={12} md={6}>
              <h5 className="mb-3" style={{ fontWeight: 600, color: '#2c3e50' }}>Lender Panel</h5>
              <Controller
                name="lender_panel_id"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <Select
                      {...field}
                      displayEmpty
                      sx={{ backgroundColor: 'white' }}
                    >
                      <MenuItem value="">
                        <em>Select Lender Panel (Optional)</em>
                      </MenuItem>
                      {lenderPanels.map((panel) => (
                        <MenuItem key={panel._id} value={panel._id}>
                          {panel.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Col>
            <Col xs={12} md={6}>
              <h5 className="mb-3" style={{ fontWeight: 600, color: '#2c3e50' }}>Partner</h5>
              <Controller
                name="partner_id"
                control={control}
                render={({ field }) => {
                  console.log('🔍 Partner Dropdown - field value:', field.value);
                  console.log('🔍 Partner Dropdown - partners:', partners);
                  return (
                    <FormControl fullWidth>
                      <Select
                        {...field}
                        displayEmpty
                        sx={{ backgroundColor: 'white' }}
                      >
                        <MenuItem value="">
                          <em>Select Partner (Optional)</em>
                        </MenuItem>
                        {partners.map((partner) => (
                          <MenuItem key={partner._id} value={partner._id}>
                            {partner.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  );
                }}
              />
            </Col>
          </Row>

          {/* Cost Calculation Preview */}
          {/* {watchedNetValue && estateCosts && (
            <Row className="mt-3">
              <Col xs={12}>
                <div className="alert alert-info">
                  <h6 className="mb-2">Cost Calculation Preview:</h6>
                  {(() => {
                    const costs = calculateCosts(parseFloat(watchedNetValue));
                    if (costs) {
                      return (
                        <>
                          <p className="mb-1">
                            <strong>Complexity:</strong> {costs.complexity} ({costs.rangeLabel})
                          </p>
                          <p className="mb-1">
                            <strong>MLM Legal Cost:</strong> £{costs.mlmLegalCost.toFixed(2)}
                          </p>
                          <p className="mb-1">
                            <strong>VAT (20%):</strong> £{costs.vat.toFixed(2)}
                          </p>
                          <p className="mb-0">
                            <strong>Total Cost:</strong> £{costs.totalCost.toFixed(2)}
                          </p>
                        </>
                      );
                    }
                    return <p>Unable to calculate costs</p>;
                  })()}
                </div>
              </Col>
            </Row>
          )} */}

          <Row className="mb-3">
            <Col xs={12}>
              <h5 className="mb-3" style={{ fontWeight: 600, color: '#2c3e50' }}>Customers</h5>
              {customerError && (
                <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                  Please select at least one customer.
                </Alert>
              )}
              {selectedCustomers.length > 0 && (
                selectedCustomers.map((customer) => (
                  <Box
                    key={customer._id}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      p: 2,
                      mb: 2,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      backgroundColor: 'white'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {customer.title} {customer.forename} {customer.surname}
                      </Typography>
                      {customer.email && (
                        <Typography variant="body2" sx={{ color: '#666', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          📧 {customer.email}
                        </Typography>
                      )}
                      {customer.phone_number && (
                        <Typography variant="body2" sx={{ color: '#666', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          📞 {customer.phone_number}
                        </Typography>
                      )}
                    </Box>
                    <Button
                      size="small"
                      onClick={() =>
                        setSelectedCustomers((prev) =>
                          prev.filter((c) => c._id !== customer._id)
                        )
                      }
                      sx={{
                        minWidth: 'auto',
                        color: '#d32f2f',
                        '&:hover': {
                          backgroundColor: '#ffebee'
                        }
                      }}
                    >
                      ✕
                    </Button>
                  </Box>
                ))
              )}
              <Button
                variant="text"
                startIcon={<PersonAddOutlinedIcon />}
                onClick={() => setModalShow(true)}
                sx={{
                  color: '#6f42c1',
                  textTransform: 'uppercase',
                  fontWeight: 'normal',
                  justifyContent: 'flex-start',
                  pl: 0
                }}
              >
                Add Customer
              </Button>
            </Col>
            <MyVerticallyCenteredModal
              show={modalShow}
              onHide={() => setModalShow(false)}
              authToken={authCtx.token}
              onCustomerAdd={handleCustomerAdd}
            />
          </Row>

          <Row>
            <Form.Group as={Col}>
              <FormControl fullWidth sx={{ my: 1 }}>
                <Stack spacing={2} direction="row">
                  <Button variant="contained" type="submit" className='red-bg red-button text-capitalize h-56'>Submit</Button>
                </Stack>
              </FormControl>
            </Form.Group>
          </Row>
        </Form>
      </div>
    </>
  );
};
